import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import './CookiePreferencesModal.css';

// Assicurati che l'elemento root della tua app sia impostato per react-modal
// È importante che questo venga chiamato una sola volta all'inizio dell'applicazione,
// tipicamente in index.js o App.js se l'App è l'unico elemento che viene montato.
// Se hai più componenti che chiamano Modal.setAppElement, potrebbe causare avvisi.
// Per questo esempio, lo lasciamo qui, ma considera di spostarlo.
Modal.setAppElement('#root');

// Definizione delle categorie di cookie
const cookieCategories = [
    {
        id: 'essential',
        name: 'Cookie Essenziali',
        description: 'Questi cookie sono necessari per il funzionamento del sito web e non possono essere disattivati nei nostri sistemi. Sono solitamente impostati solo in risposta ad azioni da te effettuate che costituiscono una richiesta di servizi, come l\'impostazione delle preferenze di privacy, l\'accesso o la compilazione di moduli. Puoi impostare il tuo browser per bloccare o avvisarti su questi cookie, ma alcune parti del sito non funzioneranno. Questi cookie non memorizzano alcuna informazione di identificazione personale.',
        alwaysEnabled: true, // Gli essenziali non possono essere disattivati
    },
    {
        id: 'analytics',
        name: 'Cookie di Analisi e Performance',
        description: 'Questi cookie ci permettono di contare le visite e le fonti di traffico, in modo da poter misurare e migliorare le prestazioni del nostro sito. Ci aiutano a sapere quali sono le pagine più e meno popolari e a vedere come i visitatori si muovono nel sito. Tutte le informazioni raccolte da questi cookie sono aggregate e quindi anonime. Se non consenti questi cookie, non sapremo quando hai visitato il nostro sito e non saremo in grado di monitorare le sue prestazioni.',
        alwaysEnabled: false,
    },
    {
        id: 'marketing',
        name: 'Cookie di Marketing e Targeting',
        description: 'Questi cookie possono essere impostati tramite il nostro sito dai nostri partner pubblicitari. Possono essere utilizzati da queste aziende per costruire un profilo dei tuoi interessi e mostrarti annunci pertinenti su altri siti. Non memorizzano direttamente informazioni personali, ma si basano sull\'identificazione univoca del tuo browser e del tuo dispositivo internet. Se non consenti questi cookie, riceverai una pubblicità meno mirata.',
        alwaysEnabled: false,
    },
    {
        id: 'externalMedia',
        name: 'Cookie per Contenuti Multimediali Esterni',
        description: 'Questi cookie sono impostati da servizi di terze parti come YouTube, Vimeo, o altri fornitori di media esterni incorporati nel nostro sito. Possono tracciare la tua attività di navigazione per scopi di marketing o per personalizzare i contenuti. Se non consenti questi cookie, i contenuti multimediali esterni potrebbero non essere visualizzati o funzionare correttamente.',
        alwaysEnabled: false,
    }
];

/**
 * Componente Modal per la gestione delle preferenze sui cookie.
 * Permette all'utente di accettare o rifiutare i cookie per categoria.
 *
 * @param {object} props - Props del componente.
 * @param {boolean} props.isOpen - Controlla la visibilità del modal.
 * @param {function} props.onRequestClose - Funzione da chiamare per chiudere il modal.
 * @param {function} props.onSavePreferences - Funzione da chiamare quando le preferenze vengono salvate.
 * @param {object} props.initialPreferences - Le preferenze attuali (dal localStorage) per inizializzare gli interruttori.
 */
function CookiePreferencesModal({ isOpen, onRequestClose, onSavePreferences, initialPreferences }) {
    // Stato locale per le preferenze, inizializzato con quelle passate o con tutti disattivati (tranne gli essenziali)
    const [preferences, setPreferences] = useState(() => {
        const defaultPrefs = {};
        cookieCategories.forEach(category => {
            // Se initialPreferences è definito, usa il suo valore, altrimenti il default (true per essenziali, false per altri)
            defaultPrefs[category.id] = category.alwaysEnabled || (initialPreferences ? initialPreferences[category.id] : false);
        });
        return defaultPrefs;
    });

    // Aggiorna lo stato locale se initialPreferences cambia (es. quando la modale si apre con nuove preferenze)
    useEffect(() => {
        if (initialPreferences) {
            const updatedPrefs = {};
            cookieCategories.forEach(category => {
                updatedPrefs[category.id] = category.alwaysEnabled || initialPreferences[category.id];
            });
            setPreferences(updatedPrefs);
        }
    }, [initialPreferences]); // Dipendenza da initialPreferences

    const handleToggle = (id) => {
        setPreferences(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const handleAcceptAll = () => {
        const allAccepted = {};
        cookieCategories.forEach(category => {
            allAccepted[category.id] = true;
        });
        setPreferences(allAccepted);
        onSavePreferences(allAccepted); // Salva le preferenze complete
        onRequestClose();
    };

    const handleRejectAll = () => {
        const allRejected = {};
        cookieCategories.forEach(category => {
            allRejected[category.id] = category.alwaysEnabled; // Essenziali rimangono attivi
        });
        setPreferences(allRejected);
        onSavePreferences(allRejected); // Salva le preferenze complete
        onRequestClose();
    };

    const handleSave = () => {
        onSavePreferences(preferences); // Salva le preferenze attuali degli interruttori
        onRequestClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className="cookie-modal-content" // Applica la classe CSS al contenuto
            overlayClassName="cookie-modal-overlay" // Applica la classe CSS all'overlay
            contentLabel="Preferenze Cookie"
            ariaHideApp={false} // Necessario per React Modal se non si setta l'elemento app globalmente o si usa #root
        >
            <div className="cookie-modal-header">
                <h2>Gestisci Preferenze Cookie</h2>
                <button onClick={onRequestClose} className="cookie-modal-close-button">
                    <FontAwesomeIcon icon={faTimes} /> {/* Icona di chiusura */}
                </button>
            </div>
            <div className="cookie-modal-body">
                <p>Utilizziamo i cookie per migliorare la tua esperienza di navigazione, offrirti contenuti personalizzati e analizzare il nostro traffico. Puoi modificare le tue preferenze in qualsiasi momento.</p>

                {cookieCategories.map(category => (
                    <div key={category.id} className="cookie-category">
                        <div className="category-header">
                            <label htmlFor={`toggle-${category.id}`}>
                                <h3>{category.name} {category.alwaysEnabled && <span className="essential-tag">(Essenziali)</span>}</h3>
                            </label>
                            <input
                                type="checkbox"
                                id={`toggle-${category.id}`}
                                checked={preferences[category.id]}
                                onChange={() => handleToggle(category.id)}
                                disabled={category.alwaysEnabled}
                                className="toggle-switch" // Applica la classe per lo stile del toggle
                            />
                        </div>
                        <p className="category-description">{category.description}</p>
                    </div>
                ))}
            </div>
            <div className="cookie-modal-footer">
                <button onClick={handleRejectAll} className="button-secondary">Rifiuta Tutti</button>
                <button onClick={handleAcceptAll} className="button-primary">Accetta Tutti</button>
                <button onClick={handleSave} className="button-tertiary">Salva Preferenze</button>
            </div>
        </Modal>
    );
}

export default CookiePreferencesModal;
